import { useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Check, Phone, Search, X } from 'lucide-react';
import * as chatApi from '../../api/chat';
import type { EligibleUser } from '../../api/chat';
import { backdropVariants, modalVariants } from '../../utils/chat/motion';
import Avatar from '../chat/Avatar';

export default function StartCallPicker({
  onClose, onPick, onPickUser, onPickMany, multi = false, excludeUserIds, title = 'Start call',
}: {
  onClose: () => void;
  /** Resolve a conversation_id for the chosen user, then start the call. */
  onPick?: (conversationId: string, user: EligibleUser) => void;
  /** Favourite mode: pick a user without creating a DM. Takes precedence over onPick. */
  onPickUser?: (user: EligibleUser) => void;
  /** Multi-select mode: returns the chosen users; caller decides DM vs group. */
  onPickMany?: (users: EligibleUser[]) => void;
  multi?: boolean;
  /** User ids to hide (e.g. people already in the call). */
  excludeUserIds?: string[];
  /** Header text + dialog label. */
  title?: string;
}) {
  const [users, setUsers] = useState<EligibleUser[]>([]);
  const [q, setQ] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  useEffect(() => {
    let alive = true;
    chatApi.getEligibleUsers().then(list => { if (alive) setUsers(list); }).catch(() => {});
    return () => { alive = false; };
  }, []);

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    const ex = new Set(excludeUserIds || []);
    let list = users.filter(u => !ex.has(u.user_id));
    if (t) list = list.filter(u => (u.name || '').toLowerCase().includes(t) || (u.email || '').toLowerCase().includes(t));
    return list;
  }, [users, q, excludeUserIds]);

  const selectedUsers = useMemo(
    () => users.filter(u => selected.has(u.user_id)),
    [users, selected],
  );

  const pick = async (u: EligibleUser) => {
    if (busyId) return;
    if (onPickUser) {
      onPickUser(u);
      onClose();
      return;
    }
    setBusyId(u.user_id);
    try {
      const conv = await chatApi.getOrCreateDm(u.user_id);
      onPick?.(conv.id, u);
      onClose();
    } catch {
      setBusyId(null);
    }
  };

  const toggle = (u: EligibleUser) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(u.user_id)) next.delete(u.user_id); else next.add(u.user_id);
      return next;
    });
  };

  const confirmMany = () => {
    if (!selected.size) return;
    const chosen = users.filter(u => selected.has(u.user_id));
    onPickMany?.(chosen);
    onClose();
  };

  return (
    <AnimatePresence>
      <motion.div
        key="backdrop"
        variants={backdropVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        onClick={onClose}
        className="gc-modal-backdrop-centered gc-callpick-backdrop"
        /* Inline z-index so the picker sits above the in-call overlay (z 9999)
         * even before the global stylesheet reloads — keeps it interactive. */
        style={{ zIndex: 10050 }}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <motion.div
          key="modal"
          variants={modalVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          onClick={(e) => e.stopPropagation()}
          className="gc-modal-shell gc-callpick"
          style={{ width: 'min(420px, calc(100vw - 28px))', maxHeight: 'min(86vh, 720px)' }}
        >
          {/* Header */}
          <div className="gc-modal-shell-header gc-callpick__header">
            <div className="gc-modal-shell-header__left">
              <span className="gc-callpick__badge"><Phone size={15} /></span>
              <h2 className="gc-modal-shell-header__title">{title}</h2>
            </div>
            <button type="button" aria-label="Close" onClick={onClose} className="gc-modal-shell-close gc-callpick__close">
              <X size={18} />
            </button>
          </div>

          {/* Search — fixed, always visible above the scroll area */}
          <div className="gc-callpick__search">
            <div className="gc-input-wrap">
              <Search size={16} className="gc-input-leading-icon gc-callpick__search-icon" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search people…"
                className="gc-callpick__search-input"
                autoFocus
              />
            </div>

            {/* Selected chips (multi only) */}
            {multi && selectedUsers.length > 0 && (
              <div className="gc-callpick__chips">
                {selectedUsers.map(u => (
                  <button
                    key={u.user_id}
                    type="button"
                    className="gc-callpick__chip"
                    onClick={() => toggle(u)}
                    title={`Remove ${u.name}`}
                  >
                    <Avatar name={u.name} size={5} src={u.avatar_url ?? null} />
                    <span className="gc-callpick__chip-name">{(u.name || '').split(' ')[0]}</span>
                    <X size={12} className="gc-callpick__chip-x" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Scrollable people list — the single scroll container */}
          <div className="gc-callpick__list">
            {filtered.length === 0 ? (
              <div className="gc-callpick__empty">No people found</div>
            ) : (
              filtered.map(u => {
                const isSel = selected.has(u.user_id);
                return (
                  <button
                    key={u.user_id}
                    type="button"
                    disabled={busyId === u.user_id}
                    onClick={() => (multi ? toggle(u) : pick(u))}
                    aria-pressed={multi ? isSel : undefined}
                    className={`gc-callpick__row${isSel ? ' is-selected' : ''}`}
                  >
                    <Avatar name={u.name} size={10} src={u.avatar_url ?? null} online={u.is_online} />
                    <span className="gc-callpick__row-text">
                      <span className="gc-callpick__row-name">{u.name}</span>
                      {u.designation && (
                        <span className="gc-callpick__row-sub">{u.designation}</span>
                      )}
                    </span>
                    {multi ? (
                      <span className={`gc-callpick__check${isSel ? ' is-checked' : ''}`} aria-hidden>
                        {isSel && <Check size={13} strokeWidth={3} />}
                      </span>
                    ) : (
                      <span className="gc-callpick__row-call" aria-hidden><Phone size={15} /></span>
                    )}
                  </button>
                );
              })
            )}
          </div>

          {/* Footer call button — fixed (multi only) */}
          {multi && (
            <div className="gc-callpick__footer">
              <button
                type="button"
                onClick={confirmMany}
                disabled={!selected.size}
                className="gc-callpick__confirm"
              >
                <Phone size={16} /> Call{selected.size ? ` (${selected.size})` : ''}
              </button>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
